import { Typography, Box, Grid, Stack, IconButton, SvgIcon } from "@mui/material";
import { useStoreConfig } from "../../contexts/StoreConfigContext";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";

// TikTok Icon SVG
const TikTokIcon = () => (
    <SvgIcon fontSize="small">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
    </SvgIcon>
);

// Social Media Buttons component
const SocialMediaButtons = () => {
    const { config } = useStoreConfig();

    // Helper function to check if URL is custom (not default homepage)
    const isCustomUrl = (url: string, defaultUrl: string) => {
        return url && url.trim() !== '' && url !== defaultUrl;
    };

    return (
        <>
            {isCustomUrl(config.socialFacebook, 'https://facebook.com') && (
                <IconButton
                    aria-label="facebook"
                    size="small"
                    sx={{ color: "#FFFFFF" }}
                    onClick={() => window.open(config.socialFacebook, "_blank")}
                >
                    <FacebookIcon fontSize="small" />
                </IconButton>
            )}
            {isCustomUrl(config.socialTwitter, 'https://twitter.com') && (
                <IconButton
                    aria-label="twitter"
                    size="small"
                    sx={{ color: "#FFFFFF" }}
                    onClick={() => window.open(config.socialTwitter, "_blank")}
                >
                    <TwitterIcon fontSize="small" />
                </IconButton>
            )}
            {isCustomUrl(config.socialInstagram, 'https://instagram.com') && (
                <IconButton
                    aria-label="instagram"
                    size="small"
                    sx={{ color: "#FFFFFF" }}
                    onClick={() => window.open(config.socialInstagram, "_blank")}
                >
                    <InstagramIcon fontSize="small" />
                </IconButton>
            )}
            {isCustomUrl(config.socialLinkedin, 'https://linkedin.com') && (
                <IconButton
                    aria-label="linkedin"
                    size="small"
                    sx={{ color: "#FFFFFF" }}
                    onClick={() => window.open(config.socialLinkedin, "_blank")}
                >
                    <LinkedInIcon fontSize="small" />
                </IconButton>
            )}
            {isCustomUrl(config.socialTiktok, 'https://tiktok.com') && (
                <IconButton
                    aria-label="tiktok"
                    size="small"
                    sx={{ color: "#FFFFFF" }}
                    onClick={() => window.open(config.socialTiktok, "_blank")}
                >
                    <TikTokIcon />
                </IconButton>
            )}
        </>
    );
};

// Copyright and Social Media section component
const CopyrightSection = () => {
    const currentYear = new Date().getFullYear();
    const { config } = useStoreConfig();

    // Helper function to check if URL is custom (not default homepage)
    const isCustomUrl = (url: string, defaultUrl: string) => {
        return url && url.trim() !== '' && url !== defaultUrl;
    };

    // Check if any social links are configured
    const hasSocialLinks =
        isCustomUrl(config.socialFacebook, 'https://facebook.com') ||
        isCustomUrl(config.socialTwitter, 'https://twitter.com') ||
        isCustomUrl(config.socialInstagram, 'https://instagram.com') ||
        isCustomUrl(config.socialLinkedin, 'https://linkedin.com') ||
        isCustomUrl(config.socialTiktok, 'https://tiktok.com');

    return (
        <Box sx={{ mb: { xs: 10, md: 12 } }}>
            <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 2, sm: 0 }}
            >
                <Grid item xs={12} sm="auto">
                    <Typography
                        variant="body2"
                        fontWeight={"bold"}
                        letterSpacing={.5}
                        sx={{
                            color: "#FFFFFF",
                            textAlign: { xs: "center", sm: "left" }
                        }}
                    >
                        © {currentYear} {config.companyName}. All rights reserved.
                    </Typography>
                </Grid>
                {hasSocialLinks && (
                    <Grid item xs={12} sm="auto">
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            justifyContent={{ xs: "center", sm: "flex-start" }}
                        >
                            <Typography
                                variant="body2"
                                fontWeight={"bold"}
                                letterSpacing={.5}
                                sx={{ color: "#FFFFFF", mr: 1 }}
                            >
                                Follow Us
                            </Typography>
                            <SocialMediaButtons />
                        </Stack>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

// Background text component
export const BackgroundText = () => {
    const { config } = useStoreConfig();

    return (
        <Typography
            variant="h1"
            sx={{
                position: "absolute",
                fontSize: { xs: "90px", sm: "170px", md: "200px" },
                bottom: { xs: "-28px", sm: "-55px", md: "-70px" },
                fontWeight: 900,
                opacity: 0.10,
                color: "#fff",
                left: "50%",
                transform: "translateX(-50%)",
                letterSpacing: "2px",
                pointerEvents: "none",
                whiteSpace: "nowrap",
                zIndex: 0,
                lineHeight: 1,
                fontFamily: "logoFont",
            }}
        >
            {config.footerAppName.toUpperCase()}
        </Typography>
    );
};

export const FooterBottomSection = () => {
    return (
        <CopyrightSection />
    );
}; 